import { useContext, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Chip, Table, TableHead, TableBody, TableRow, TableCell, Typography, Hidden, Button, Link, Tooltip, Grid, CircularProgress, Drawer } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import SettingsIcon from '@material-ui/icons/Settings'
import SpeedIcon from '@material-ui/icons/Speed'
import PeopleAltIcon from '@material-ui/icons/PeopleAlt'
import { NavContext, NavRoutes, UserContext } from '../contexts';
import { RequestStatus, getRequestPerformanceText, getRequestStatusText } from '../../helpers';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({  
    skeleton: {
        margin: theme.spacing(1)
    },
    viewall: {
        marginLeft: theme.spacing(2)
    },
    blackHeader: {
        backgroundColor:'#333333',
    },
    whiteHeaderLabel: {
        color: "white"
    },
    performanceChip: {
        marginTop: theme.spacing(1)
    },
    buttonIcon: {
        marginRight: theme.spacing(1)
    },
    drawerHeight: {
        height:"70px"
    },
    drawerButton: {
        height:"100%",
        width:"100%"
    }
}));

const LinkRouter = props => <Link {...props} component={RouterLink} />;

export const RequestsList = (props) => {
    const classes = useStyles();
    const { getRouteTo } = useContext(NavContext);
    const { userInfo } = useContext(UserContext); 

    const [tooltipStates, setTooltipStates] = useState([]);
    const [selectedRequest, setSelectedRequest] = useState(null);

    const setTooltipState = (customerRequestId, isOpen) => {
        const tpstates = [].concat(tooltipStates).filter(x => x.customerRequestId !== customerRequestId);
        tpstates.push({ customerRequestId, isOpen});
        setTooltipStates(tpstates);
    };

    const getTooltipState = customerRequestId => {
        const tps = tooltipStates.find(x => x.customerRequestId === customerRequestId);
        return !tps || (tps && tps.isOpen);
    };

    const rowClickHandler = index => {
        if (index !== null){
            if (index === selectedRequest)
                setSelectedRequest(null);
            else
                setSelectedRequest(index);
        }
    };

    const deleteClickHandler = async (index?) => {
        if (index !== null || index !== undefined)
            await props.deleteHandler(props.requests[index]);
        else {
            await props.deleteHandler(props.requests[selectedRequest]);
            setSelectedRequest(null);
        }
    };

    const renderInvolvedUsers = (customerRequestId, requestStatus) => {
        const users = props.involvedUsers.getInvolvedUsers(customerRequestId);

        if (users.length > 0){
            if (requestStatus !== RequestStatus.Logistic){
                return users.map((x, index: number): JSX.Element =>
                    <Typography
                        key={index}
                        component="div"
                    >{x}</Typography>
                );
            }

            else {
                const involvedUsersInfo = [];

                users.map(user => {
                    const splitUser = user.split(" (");
                    const userObject = {
                        name: splitUser[0],
                        role: splitUser[1].replace(")", "")
                    };
                    involvedUsersInfo.push(userObject);
                });
            
                involvedUsersInfo.sort((a, b) => a.role > b.role ? 1 : -1);
                
                return involvedUsersInfo.map((user, index: number): JSX.Element =>
                    <>
                        {
                            index !== 0 &&
                            involvedUsersInfo[index].role !== involvedUsersInfo[index - 1].role &&
                            <Typography
                                component="div"
                                style={{color: 'transparent'}}
                            >-</Typography>
                        }

                        {
                            (
                                index === 0 ||
                                involvedUsersInfo[index].role !== involvedUsersInfo[index - 1].role
                            ) &&
                            <Typography
                                component="div"
                            >
                                {user.role.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()) + ":"}
                            </Typography>
                        }

                        <Typography component="div">
                            {user.name}
                        </Typography>
                    </>
                );
            }
        }
    };

    return (
        <>
            <Hidden smDown>
                <Table>
                    <TableHead>
                        <TableRow className={props.blackHeader ? classes.blackHeader : null}>
                            {
                                props.requestId &&
                                <TableCell className={props.blackHeader ? classes.whiteHeaderLabel : null}>
                                    #
                                </TableCell>
                            }

                            <TableCell className={props.blackHeader ? classes.whiteHeaderLabel : null}>
                                Customer
                            </TableCell>
                            <TableCell className={props.blackHeader ? classes.whiteHeaderLabel : null}>
                                Opener
                            </TableCell>
                            <TableCell className={props.blackHeader ? classes.whiteHeaderLabel : null}>
                                Status
                            </TableCell>
                            <TableCell className={props.blackHeader ? classes.whiteHeaderLabel : null}>
                                Duration
                            </TableCell>

                            {
                                props.inChargeOf &&
                                <TableCell
                                    align="center"
                                    className={props.blackHeader ? classes.whiteHeaderLabel : null}
                                >In charge of</TableCell>
                            }

                            {
                                props.attachments &&
                                <TableCell className={props.blackHeader ? classes.whiteHeaderLabel : null}>
                                    Attachments
                                </TableCell>
                            }

                            {
                                props.delete &&
                                <TableCell></TableCell>
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            props.requests &&
                            props.requests.length > 0 &&
                            props.requests.map((request, index: number): JSX.Element =>
                                <TableRow key={index}>
                                    {
                                        props.requestId &&
                                        <TableCell component="th" scope="row">
                                            <Typography>
                                                {request.customerRequestId}
                                            </Typography>
                                        </TableCell>
                                    }
                                    <TableCell component="th" scope="row">
                                        <Typography>                                    
                                            <LinkRouter
                                                key={index}
                                                color="textSecondary"
                                                to={getRouteTo(NavRoutes.EditRequest, [request.customerRequestId])}
                                            >
                                                {request.customerName}
                                            </LinkRouter>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography>
                                            {
                                                request.ownerKey !== userInfo.externalKey ?
                                                request.ownerFullName :
                                                "YOU"
                                            }
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={<SettingsIcon />} 
                                            label={getRequestStatusText(request.status)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            color="primary" 
                                            variant="outlined" 
                                            icon={<SpeedIcon />} 
                                            label={getRequestPerformanceText(request.status, request.performance)}
                                        />
                                    </TableCell>

                                    {
                                        props.inChargeOf &&
                                        <TableCell align="center">
                                            {
                                                !props.involvedUsers.hasLodedInvolvedUsers(request.customerRequestId) ?
                                                <PeopleAltIcon
                                                    onClick={async () =>
                                                        await props.involvedUsers.loadInvolvedUsers(request.customerRequestId, request.status, request.dbType)
                                                    }
                                                /> :
                                                props.involvedUsers.isLoadingInvolvedUsers(request.customerRequestId) ?
                                                <CircularProgress size="26px" /> :
                                                <Tooltip
                                                    open={getTooltipState(request.customerRequestId)}
                                                    title={renderInvolvedUsers(request.customerRequestId, request.status)} 
                                                    aria-label="people" 
                                                    arrow
                                                    enterTouchDelay={1}
                                                    onOpen={() => setTooltipState(request.customerRequestId, true)}
                                                    onClose={() => setTooltipState(request.customerRequestId, false)}
                                                >
                                                    <PeopleAltIcon />
                                                </Tooltip>
                                            }
                                        </TableCell>
                                    }

                                    {
                                        props.attachments &&
                                        <TableCell>
                                            {
                                                request.sharepointFolderUrl ?
                                                <Link
                                                    key={index}
                                                    color="textSecondary"
                                                    href={request.sharepointFolderUrl}
                                                    target="_blank"
                                                    rel="noopener"
                                                >
                                                    <Typography>See attachments</Typography>
                                                </Link> :
                                                <Typography variant="subtitle1">Not Available</Typography>
                                            }
                                        </TableCell>
                                    }

                                    {
                                        props.delete &&
                                        props.deleteHandler &&
                                        <TableCell align="right">
                                            <Button                                     
                                                color="secondary"
                                                disabled={props.isDisabled}
                                                size="small"     
                                                variant="contained"
                                                onClick={() => deleteClickHandler(index)}
                                            >Delete</Button>
                                        </TableCell>
                                    }
                                </TableRow>
                            )
                        }
                    </TableBody>
                </Table>
            </Hidden>
            <Hidden mdUp>
                <Table>
                    <TableHead>
                        <TableRow className={props.blackHeader ? classes.blackHeader : null}>
                            <TableCell className={props.blackHeader ? classes.whiteHeaderLabel : null}>
                                Request
                            </TableCell>
                            <TableCell className={props.blackHeader ? classes.whiteHeaderLabel : null}>
                                Info
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            props.requests &&
                            props.requests.length > 0 &&
                            props.requests.map((request, index: number): JSX.Element =>
                                <TableRow
                                    key={index}
                                    hover
                                    onClick={() => rowClickHandler(index)}
                                    selected={selectedRequest === index && props.delete && props.deleteHandler}
                                >
                                    <TableCell component="th" scope="row">
                                        <Typography>                                    
                                            <LinkRouter
                                                key={index}
                                                color="textSecondary"
                                                to={getRouteTo(NavRoutes.EditRequest, [request.customerRequestId])}
                                            >
                                                {request.customerName}
                                            </LinkRouter>
                                        </Typography>
                                        <Typography>
                                            {
                                                request.ownerKey !== userInfo.externalKey ?
                                                request.ownerFullName :
                                                "YOU"
                                            }
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12}>
                                                <Chip
                                                    icon={<SettingsIcon />} 
                                                    label={getRequestStatusText(request.status)}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Chip
                                                    color="primary" 
                                                    variant="outlined" 
                                                    icon={<SpeedIcon />} 
                                                    label={getRequestPerformanceText(request.status, request.performance)}
                                                />
                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                </TableRow>
                            )
                        }
                    </TableBody>
                </Table>
                {
                    props.delete &&
                    props.deleteHandler &&
                    <Drawer
                        anchor="bottom"
                        open={selectedRequest !== null}
                        PaperProps={{ elevation: 1 }}
                        variant="persistent"
                    >
                        <Grid
                            className={classes.drawerHeight}
                            container
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Grid className={classes.drawerButton} item>
                                <Button
                                    disabled={props.isDisabled}
                                    onClick={() => deleteClickHandler()}
                                    className={classes.drawerButton}
                                >
                                    <DeleteIcon className={classes.buttonIcon} />
                                    Delete
                                </Button>
                            </Grid>
                        </Grid>
                    </Drawer>
                }
            </Hidden>
        </>
    );
}